import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  FavoriteBorder as FavoriteBorderIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  EditNote as EditNoteIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import Swal from 'sweetalert2';

const PROTOCOL_NATURAL = 'NATURAL';
const PROTOCOL_MODIFIED = 'MODIFIED';
const PROTOCOL_HRT = 'HRT';
const PROTOCOL_STIMULATED = 'STIMULATED';

// Helper function to format date to dd/mm/yyyy
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '-';
  }
};

const EmbryoFetMonitoringModal = ({
  open,
  onClose,
  procedureId,
  visitId,
  contactId,
  primaryColor = '#00798E',
}) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('v1-embryo-fet-monitoring-create');
  const canEdit = hasPermission('v1-embryo-fet-monitoring-update');
  const canDelete = hasPermission('v1-embryo-fet-monitoring-delete');

  const [view, setView] = useState('list'); // 'list', 'create', 'records'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [monitorings, setMonitorings] = useState([]);
  const [selectedMonitoring, setSelectedMonitoring] = useState(null);
  const [paramMembers, setParamMembers] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  // Create monitoring form - Updated with estimated_date field
  const [monitoringForm, setMonitoringForm] = useState({
    diagnosis: '',
    embryo_stage: '',
    no_of_embryos: '',
    protocol: '',
    lmp: '',
    estimated_date: '',
    contact_id: contactId || null,
    visit_id: visitId || null,
  });

  // Record form with dynamic fields
  const [recordForm, setRecordForm] = useState({
    date: '',
    cycle_day: '',
    scan: '',
    endometrium: '',
    ovulation_status: '',
    follicle: '',
    estrogen_dose: '',
    progesterone_start: '',
    drugs: '',
    remarks: '',
  });

  const [records, setRecords] = useState([]);
  const [editingRecordId, setEditingRecordId] = useState(null);

  // Endometrial Acceptability Check - Map clause to acceptance status
  const [endometrialForm, setEndometrialForm] = useState({});
  const [endometrialAcceptabilities, setEndometrialAcceptabilities] = useState([]);
  const [editingEndometrialId, setEditingEndometrialId] = useState(null);
  const [clauseOptions, setClauseOptions] = useState([]);

  // Load monitoring data
  useEffect(() => {
    if (open && view === 'list') {
      loadMonitorings();
      if (contactId) {
        loadPatientInfo();
      }
    }
  }, [open, view, contactId, procedureId]);

  // Load patient information
  const loadPatientInfo = async () => {
    setLoadingPatient(true);
    try {
      const response = await api.contacts.getContactById(contactId);
      if (response?.data) {
        setPatientInfo(response.data);
      }
    } catch (error) {
      console.error('Error loading patient info:', error);
    } finally {
      setLoadingPatient(false);
    }
  };

  // Load FET parameters
  useEffect(() => {
    const loadParams = async () => {
      try {
        const response = await api.embryoFetMonitoring.getFetMonitoringParameterGroup();
        if (response?.data?.paramMembers) {
          setParamMembers(response.data.paramMembers);
          
          // Get endometrial clause options
          const clauseOpts = response.data.paramMembers.find((p) => p.slug === 'fet-endometrial-acceptability-clause')?.members || [];
          setClauseOptions(clauseOpts);
          
          // Initialize form with clause options
          const initialForm = {};
          clauseOpts.forEach((opt) => {
            initialForm[opt.value] = false; // false = rejected, true = accepted
          });
          setEndometrialForm(initialForm);
        }
      } catch (error) {
        console.error('Error loading FET parameters:', error);
      }
    };
    loadParams();
  }, []);

  const loadMonitorings = async () => {
    setLoading(true);
    try {
      const response = await api.embryoFetMonitoring.getFetMonitoringsByProcedure(procedureId);
      // Handle different response structures
      const data = response?.data || response || [];
      setMonitorings(Array.isArray(data) ? data : []);
      console.log('FET Monitorings loaded:', data);
    } catch (error) {
      console.error('Error loading monitorings:', error);
      setMonitorings([]);
      Swal.fire('Error', 'Failed to load FET monitoring data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async (monitoringId) => {
    try {
      const response = await api.embryoFetMonitoring.getFetMonitoring(monitoringId);
      if (response?.data?.records) {
        setRecords(response.data.records);
      }
    } catch (error) {
      console.error('Error loading records:', error);
    }
  };

  const handleCreateMonitoring = async () => {
    if (!monitoringForm.diagnosis || !monitoringForm.embryo_stage || !monitoringForm.no_of_embryos || !monitoringForm.protocol) {
      Swal.fire('Validation Error', 'Please fill all required fields (Diagnosis, Embryo Stage, No. of Embryos, Protocol)', 'warning');
      return;
    }

    setSaving(true);
    try {
      await api.embryoFetMonitoring.createFetMonitoring(procedureId, {
        monitoring: {
          ...monitoringForm,
          contact_id: contactId,
          visit_id: visitId,
        },
        records: [],
        acceptabilities: [],
      });
      Swal.fire('Success', 'FET Monitoring created successfully', 'success');
      resetForm();
      loadMonitorings();
      setView('list');
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to create FET monitoring', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecord = async () => {
    const recordData = getRecordDataByProtocol();
    if (!recordData.date || !recordData.cycle_day) {
      Swal.fire('Validation Error', 'Date and Cycle Day are required', 'warning');
      return;
    }

    setSaving(true);
    try {
      if (editingRecordId) {
        // Update record
        const updatedRecords = records.map((r) =>
          r.id === editingRecordId ? { ...r, ...recordData, updated_by: 1 } : r
        );
        await api.embryoFetMonitoring.updateFetMonitoringRecords(selectedMonitoring.id, updatedRecords);
      } else {
        // Add new record
        const newRecords = [...records, { ...recordData, created_by: 1 }];
        await api.embryoFetMonitoring.updateFetMonitoringRecords(selectedMonitoring.id, newRecords);
      }
      Swal.fire('Success', editingRecordId ? 'Record updated' : 'Record added', 'success');
      resetRecordForm();
      loadRecords(selectedMonitoring.id);
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to save record', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditRecord = (record) => {
    setRecordForm(record);
    setEditingRecordId(record.id);
  };

  const handleDeleteRecord = async (recordId) => {
    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'This record will be deleted',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
    });

    if (!confirmed.isConfirmed) return;

    setSaving(true);
    try {
      const updatedRecords = records.filter((r) => r.id !== recordId);
      await api.embryoFetMonitoring.updateFetMonitoringRecords(selectedMonitoring.id, updatedRecords);
      Swal.fire('Success', 'Record deleted', 'success');
      loadRecords(selectedMonitoring.id);
    } catch (error) {
      Swal.fire('Error', 'Failed to delete record', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getRecordDataByProtocol = () => {
    const baseData = {
      date: recordForm.date,
      cycle_day: recordForm.cycle_day,
      scan: recordForm.scan,
      endometrium: recordForm.endometrium,
      remarks: recordForm.remarks,
    };

    switch (selectedMonitoring?.protocol) {
      case PROTOCOL_NATURAL:
        return {
          ...baseData,
          ovulation_status: recordForm.ovulation_status,
          drugs: recordForm.drugs,
        };
      case PROTOCOL_MODIFIED:
        return {
          ...baseData,
          follicle: recordForm.follicle,
          drugs: recordForm.drugs,
        };
      case PROTOCOL_HRT:
        return {
          ...baseData,
          estrogen_dose: recordForm.estrogen_dose,
          progesterone_start: recordForm.progesterone_start,
        };
      case PROTOCOL_STIMULATED:
        return {
          ...baseData,
          follicle: recordForm.follicle,
          drugs: recordForm.drugs,
        };
      default:
        return baseData;
    }
  };

  const resetForm = () => {
    setMonitoringForm({
      diagnosis: '',
      embryo_stage: '',
      no_of_embryos: '',
      protocol: '',
      lmp: '',
      estimated_date: '',
      contact_id: contactId || null,
      visit_id: visitId || null,
    });
  };

  const resetRecordForm = () => {
    setRecordForm({
      date: '',
      cycle_day: '',
      scan: '',
      endometrium: '',
      ovulation_status: '',
      follicle: '',
      estrogen_dose: '',
      progesterone_start: '',
      drugs: '',
      remarks: '',
    });
    setEditingRecordId(null);
  };

  const handleSelectMonitoring = (monitoring) => {
    setSelectedMonitoring(monitoring);
    setRecords(monitoring.records || []);
    setView('records');
    loadRecords(monitoring.id);
  };

  const handleEditMonitoring = (monitoring) => {
    setSelectedMonitoring(monitoring);
    setMonitoringForm({
      diagnosis: monitoring.diagnosis || '',
      embryo_stage: monitoring.embryo_stage || '',
      no_of_embryos: monitoring.no_of_embryos || '',
      protocol: monitoring.protocol || '',
      lmp: monitoring.lmp || '',
      estimated_date: monitoring.estimated_date || '',
      contact_id: contactId || null,
      visit_id: visitId || null,
    });
    setView('edit');
  };

  const handleUpdateMonitoring = async () => {
    if (!monitoringForm.diagnosis || !monitoringForm.embryo_stage || !monitoringForm.no_of_embryos || !monitoringForm.protocol) {
      Swal.fire('Validation Error', 'Please fill all required fields (Diagnosis, Embryo Stage, No. of Embryos, Protocol)', 'warning');
      return;
    }

    setSaving(true);
    try {
      await api.embryoFetMonitoring.updateFetMonitoring(selectedMonitoring.id, {
        ...monitoringForm,
        irm_procedures_id: procedureId,
        contact_id: contactId,
        visit_id: visitId,
      });
      Swal.fire('Success', 'FET Monitoring updated successfully', 'success');
      loadMonitorings();
      setView('list');
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to update FET monitoring', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Load Endometrial Acceptabilities
  const loadEndometrialAcceptabilities = async (monitoringId) => {
    try {
      const response = await api.embryoFetMonitoring.getEndometrialAcceptabilities(monitoringId);
      if (response?.data) {
        setEndometrialAcceptabilities(response.data);
        
        // Build form state from latest records for each clause
        const latestByClause = {};
        response.data.forEach((record) => {
          if (!latestByClause[record.clause] || new Date(record.created_at) > new Date(latestByClause[record.clause].created_at)) {
            latestByClause[record.clause] = record;
          }
        });
        
        // Initialize form with checked/unchecked status from latest records
        const formState = { notes: '' };
        clauseOptions.forEach((opt) => {
          if (latestByClause[opt.value]) {
            formState[opt.value] = latestByClause[opt.value].acceptance === 'Accepted';
          } else {
            formState[opt.value] = false;
          }
        });
        setEndometrialForm(formState);
      }
    } catch (error) {
      console.error('Error loading endometrial acceptabilities:', error);
    }
  };

  // Handle Add/Update Endometrial Acceptability
  const handleAddEndometrialAcceptability = async () => {
    setSaving(true);
    try {
      // Create records for each clause with current acceptance status
      const newRecords = [];
      Object.entries(endometrialForm).forEach(([clauseName, isAccepted]) => {
        // Skip the 'notes' key - it's not a clause
        if (clauseName === 'notes') return;
        
        const acceptance = isAccepted ? 'Accepted' : 'Rejected';
        newRecords.push({
          clause: clauseName,
          acceptance: acceptance,
          notes: endometrialForm.notes || '',
        });
      });

      // If editing, mark old records as deleted
      let updatedAcceptabilities = [...endometrialAcceptabilities];
      if (editingEndometrialId) {
        updatedAcceptabilities = updatedAcceptabilities.map(item => {
          if (item.id === editingEndometrialId) {
            return { ...item, _deleted: true };
          }
          return item;
        });
      }

      // Add new records
      updatedAcceptabilities = [...updatedAcceptabilities, ...newRecords];

      await api.embryoFetMonitoring.updateEndometrialAcceptabilities(
        selectedMonitoring.id, 
        updatedAcceptabilities
      );

      Swal.fire('Success', 'Endometrial acceptability recorded', 'success');
      resetEndometrialForm();
      loadEndometrialAcceptabilities(selectedMonitoring.id);
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to save endometrial acceptability', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditEndometrialAcceptability = (item) => {
    // Set form to editing state of this record
    const formState = {};
    let notesForEditing = '';
    endometrialAcceptabilities.forEach((record) => {
      if (record.id === item.id) {
        formState[record.clause] = record.acceptance === 'Accepted';
        notesForEditing = record.notes || '';
      }
    });
    formState.notes = notesForEditing;
    setEndometrialForm(formState);
    setEditingEndometrialId(item.id);
  };

  const resetEndometrialForm = () => {
    const initialForm = { notes: '' };
    clauseOptions.forEach((opt) => {
      initialForm[opt.value] = false;
    });
    setEndometrialForm(initialForm);
    setEditingEndometrialId(null);
  };

  const protocolOptions = paramMembers.find((p) => p.slug === 'fet-protocol')?.members || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* ── DIALOG HEADER WITH PATIENT INFO ── */}
      <DialogTitle sx={{ 
        backgroundColor: primaryColor, 
        color: 'white !important',
        padding: '16px 24px',
        borderBottom: `3px solid ${primaryColor}`
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          {/* Left side: Navigation + Title */}
          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
            {view !== 'list' && (
              <IconButton
                size="small"
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                onClick={() => {
                  if (view === 'records') {
                    setView('list');
                    resetRecordForm();
                  } else {
                    setView('list');
                    resetForm();
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', mb: 0.3 }}>
                FET Monitoring
              </Typography>
              {view === 'create' && (
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', fontWeight: 500 }}>
                  Create New Record
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right side: Patient Info + Create Button + Close */}
          <Box display="flex" alignItems="center" gap={1.5}>
            {patientInfo && (
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.15)', 
                borderRadius: '6px', 
                padding: '8px 12px',
                display: 'flex',
                gap: 2,
                fontSize: '0.85rem',
                color: 'white'
              }}>
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600, mb: 0.3 }}>Name</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{patientInfo.name || '—'}</Typography>
                </Box>
                <Divider orientation="vertical" sx={{ backgroundColor: 'rgba(255,255,255,0.3)', width: '1px' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600, mb: 0.3 }}>Age</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{patientInfo.age || '—'}</Typography>
                </Box>
                <Divider orientation="vertical" sx={{ backgroundColor: 'rgba(255,255,255,0.3)', width: '1px' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600, mb: 0.3 }}>DOB</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{patientInfo.date_of_birth || '—'}</Typography>
                </Box>
                <Divider orientation="vertical" sx={{ backgroundColor: 'rgba(255,255,255,0.3)', width: '1px' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600, mb: 0.3 }}>Gender</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{patientInfo.gender || '—'}</Typography>
                </Box>
                <Divider orientation="vertical" sx={{ backgroundColor: 'rgba(255,255,255,0.3)', width: '1px' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600, mb: 0.3 }}>ID</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{patientInfo.external_code || patientInfo.code || '—'}</Typography>
                </Box>
              </Box>
            )}
            {view === 'list' && canCreate && (
              <Button
                variant="outlined"
                startIcon={<AddIcon sx={{ color: 'white' }} />}
                onClick={() => {
                  resetForm();
                  setView('create');
                }}
                sx={{ 
                  backgroundColor: 'transparent',
                  color: 'white',
                  borderColor: 'white',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderColor: 'white'
                  }
                }}
              >
                Create New
              </Button>
            )}
            <IconButton
              size="small"
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3, backgroundColor: '#fafafa' }}>
        {/* LIST VIEW */}
        {view === 'list' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor, mb: 1 }}>
              &nbsp;
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress sx={{ color: primaryColor }} />
              </Box>
            ) : monitorings.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  px: 3,
                  backgroundColor: `${primaryColor}08`,
                  borderRadius: 2,
                  border: `2px dashed ${primaryColor}40`,
                }}
              >
                <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600, mb: 1 }}>
                  No FET Monitoring Records Yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', textAlign: 'center' }}>
                  Start tracking embryo transfer by creating your first monitoring record
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid ${primaryColor}30` }}>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow sx={{ height: 56, backgroundColor: `${primaryColor}15` }}>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>
                        Diagnosis
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>
                        Protocol
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>
                        Embryo Stage
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                        textAlign: 'center'
                      }}>
                        Embryos
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>
                        LMP
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>
                        Est. Date
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                        textAlign: 'center'
                      }}>
                        Records
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                        textAlign: 'center'
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                    {monitorings && Array.isArray(monitorings) && monitorings.length > 0 ? (
                      monitorings.map((arkaM, idx) => {
                        const m = arkaM.monitoring || arkaM; // Handle both structures
                        return (
                          <TableRow 
                            key={m?.id ? `monitoring-${m.id}` : `monitoring-idx-${idx}`}
                            sx={{ 
                              '&:hover': { backgroundColor: `${primaryColor}08` },
                              borderBottom: `1px solid ${primaryColor}20`
                          }}
                        >
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem' }}>
                            {m?.diagnosis ? (
                              <Box 
                                dangerouslySetInnerHTML={{__html: m.diagnosis}}
                                sx={{ 
                                  maxHeight: '80px', 
                                  overflow: 'auto', 
                                  '& p, & div, & span': { margin: 0 },
                                  '& *': { fontSize: '0.85rem' }
                                }}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem' }}>
                            {m?.protocol ? (
                              <Chip 
                                label={m.protocol} 
                                size="small"
                                sx={{
                                  backgroundColor: `${primaryColor}20`,
                                  color: primaryColor,
                                  fontWeight: 600,
                                  height: 24
                                }}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem' }}>{m?.embryo_stage || '-'}</TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem', textAlign: 'center' }}>{m?.no_of_embryos || '-'}</TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem' }}>{formatDateToDDMMYYYY(m?.lmp)}</TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem' }}>{formatDateToDDMMYYYY(m?.estimated_date)}</TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.9rem', textAlign: 'center' }}>
                            <Chip 
                              label={(m?.records || []).length} 
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: primaryColor, color: primaryColor, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                            {canEdit && (
                              <Tooltip title="Edit Monitoring">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditMonitoring(m)}
                                  sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}15` } }}
                                >
                                  <EditNoteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View/Add Records">
                              <IconButton
                                size="small"
                                onClick={() => handleSelectMonitoring(m)}
                                sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}15` } }}
                              >
                                <ListAltIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f15' } }}
                                  onClick={async () => {
                                    const confirmed = await Swal.fire({
                                      title: 'Delete Monitoring?',
                                      text: 'This action cannot be undone',
                                      icon: 'warning',
                                      showCancelButton: true,
                                      confirmButtonText: 'Yes, delete',
                                      confirmButtonColor: '#d32f2f',
                                      cancelButtonText: 'Cancel',
                                    });
                                    if (confirmed.isConfirmed) {
                                      try {
                                        await api.embryoFetMonitoring.deleteFetMonitoring(m.id);
                                        Swal.fire('Success', 'Monitoring deleted', 'success');
                                        loadMonitorings();
                                      } catch (error) {
                                        Swal.fire('Error', 'Failed to delete', 'error');
                                      }
                                    }
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, color: '#999' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* CREATE VIEW - REDESIGNED FORM */}
        {view === 'create' && (
          <Box sx={{ backgroundColor: 'white', borderRadius: '8px', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: primaryColor, display: 'flex', alignItems: 'center', gap: 1 }}>
              ➕ Create New FET Monitoring Record
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2.5}>
              {/* Diagnosis - RichTextEditor */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1, color: primaryColor }}>
                  Diagnosis <span style={{ color: 'red' }}>*</span>
                </Typography>
                <RichTextEditor
                  label=""
                  value={monitoringForm.diagnosis || ''}
                  onChange={(value) => setMonitoringForm({ ...monitoringForm, diagnosis: value })}
                  placeholder="Enter diagnosis details..."
                />
              </Grid>

              {/* Embryo Stage and No of Embryos */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Embryo Stage *"
                  placeholder="e.g., Day 5 Blastocyst"
                  value={monitoringForm.embryo_stage}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, embryo_stage: e.target.value })}
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Embryos *"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={monitoringForm.no_of_embryos}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, no_of_embryos: e.target.value })}
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Protocol and LMP */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ 
                    '&.Mui-focused': { color: primaryColor }
                  }}>Protocol *</InputLabel>
                  <Select
                    value={monitoringForm.protocol}
                    label="Protocol *"
                    onChange={(e) => setMonitoringForm({ ...monitoringForm, protocol: e.target.value })}
                    sx={{
                      borderRadius: '6px',
                      height: '40px',
                      minHeight: '40px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: primaryColor,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: primaryColor,
                        },
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          borderRadius: '6px',
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Protocol</em>
                    </MenuItem>
                    {protocolOptions && protocolOptions.map((opt, idx) => (
                      <MenuItem key={opt?.id ? `proto-${opt.id}` : `proto-idx-${idx}`} value={opt.value?.toUpperCase()}>
                        {opt.value?.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="LMP (Last Menstrual Period)"
                  value={monitoringForm.lmp}
                  onChange={(date) => setMonitoringForm({ ...monitoringForm, lmp: date })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Grid>

              {/* Estimated Date */}
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="Estimated Transfer Date"
                  value={monitoringForm.estimated_date}
                  onChange={(date) => setMonitoringForm({ ...monitoringForm, estimated_date: date })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* EDIT VIEW - REDESIGNED FORM */}
        {view === 'edit' && selectedMonitoring && (
          <Box sx={{ backgroundColor: 'white', borderRadius: '8px', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: primaryColor, display: 'flex', alignItems: 'center', gap: 1 }}>
              ✎ Edit FET Monitoring Record
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2.5}>
              {/* Diagnosis - RichTextEditor */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1, color: primaryColor }}>
                  Diagnosis <span style={{ color: 'red' }}>*</span>
                </Typography>
                <RichTextEditor
                  label=""
                  value={monitoringForm.diagnosis || ''}
                  onChange={(value) => setMonitoringForm({ ...monitoringForm, diagnosis: value })}
                  placeholder="Enter diagnosis details..."
                />
              </Grid>

              {/* Embryo Stage and No of Embryos */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Embryo Stage *"
                  placeholder="e.g., Day 5 Blastocyst"
                  value={monitoringForm.embryo_stage}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, embryo_stage: e.target.value })}
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Embryos *"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={monitoringForm.no_of_embryos}
                  onChange={(e) => setMonitoringForm({ ...monitoringForm, no_of_embryos: e.target.value })}
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Protocol and LMP */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ 
                    '&.Mui-focused': { color: primaryColor }
                  }}>Protocol *</InputLabel>
                  <Select
                    value={monitoringForm.protocol}
                    label="Protocol *"
                    onChange={(e) => setMonitoringForm({ ...monitoringForm, protocol: e.target.value })}
                    sx={{
                      borderRadius: '6px',
                      height: '40px',
                      minHeight: '40px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: primaryColor,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: primaryColor,
                        },
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          borderRadius: '6px',
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Protocol</em>
                    </MenuItem>
                    {protocolOptions && protocolOptions.map((opt, idx) => (
                      <MenuItem key={opt?.id ? `proto-${opt.id}` : `proto-idx-${idx}`} value={opt.value?.toUpperCase()}>
                        {opt.value?.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="LMP (Last Menstrual Period)"
                  value={monitoringForm.lmp}
                  onChange={(date) => setMonitoringForm({ ...monitoringForm, lmp: date })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Grid>

              {/* Estimated Date */}
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="Estimated Transfer Date"
                  value={monitoringForm.estimated_date}
                  onChange={(date) => setMonitoringForm({ ...monitoringForm, estimated_date: date })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* RECORDS VIEW */}
        {view === 'records' && selectedMonitoring && (
          <Box>
            <Card sx={{ 
              mt: 2,
              mb: 3, 
              backgroundColor: `${primaryColor}12`,
              border: `2px solid ${primaryColor}30`
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>
                    📋 Monitoring Details
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        color: primaryColor,
                        borderColor: primaryColor,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        '&:hover': {
                          backgroundColor: `${primaryColor}10`,
                          borderColor: primaryColor,
                        }
                      }}
                      onClick={() => {
                        loadEndometrialAcceptabilities(selectedMonitoring.id);
                        setView('endometrial');
                      }}
                    >
                      👁️ View Endometrial Acceptability
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        backgroundColor: primaryColor,
                        color: 'white !important',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        '&:hover': {
                          backgroundColor: primaryColor,
                          opacity: 0.9,
                        }
                      }}
                      onClick={() => {
                        loadEndometrialAcceptabilities(selectedMonitoring.id);
                        setView('endometrial');
                      }}
                    >
                      ✏️ Add/Edit Endometrial Acceptability
                    </Button>
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" component="strong">Diagnosis:</Typography>
                      <Box
                        component="div"
                        sx={{ 
                          variant: 'body2',
                          '& p': { margin: 0, display: 'inline' },
                          '& *': { display: 'inline' }
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedMonitoring.diagnosis || '-' }}
                      />
                    </Box>
                    <Typography variant="body2"><strong>Embryo Stage:</strong> {selectedMonitoring.embryo_stage}</Typography>
                    <Typography variant="body2"><strong>Number of Embryos:</strong> {selectedMonitoring.no_of_embryos}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Protocol:</strong> 
                      <Chip 
                        label={selectedMonitoring.protocol} 
                        size="small" 
                        sx={{ 
                          ml: 1,
                          backgroundColor: `${primaryColor}20`,
                          color: primaryColor,
                          fontWeight: 600
                        }} 
                      />
                    </Typography>
                    <Typography variant="body2"><strong>LMP Date:</strong> {formatDateToDDMMYYYY(selectedMonitoring.lmp)}</Typography>
                    <Typography variant="body2"><strong>Estimated Date:</strong> {formatDateToDDMMYYYY(selectedMonitoring.estimated_date)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>
                📅 Daily Records ({records.length})
              </Typography>
            </Box>

            {/* RECORD FORM */}
            <Card sx={{ 
              mb: 3, 
              border: `2px solid ${primaryColor}`,
              backgroundColor: `${primaryColor}05`
            }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: primaryColor }}>
                  {editingRecordId ? '✎ Edit Record' : '➕ Add New Record'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDDatePicker
                      label="Date *"
                      value={recordForm.date}
                      onChange={(date) => setRecordForm({ ...recordForm, date })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cycle Day *"
                      type="number"
                      value={recordForm.cycle_day}
                      onChange={(e) => setRecordForm({ ...recordForm, cycle_day: e.target.value })}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Scan"
                      value={recordForm.scan}
                      onChange={(e) => setRecordForm({ ...recordForm, scan: e.target.value.toUpperCase() })}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Endometrium"
                      value={recordForm.endometrium}
                      onChange={(e) => setRecordForm({ ...recordForm, endometrium: e.target.value.toUpperCase() })}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* NATURAL PROTOCOL */}
                  {selectedMonitoring.protocol === PROTOCOL_NATURAL && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Ovulation Status"
                          value={recordForm.ovulation_status}
                          onChange={(e) => setRecordForm({ ...recordForm, ovulation_status: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Drugs"
                          value={recordForm.drugs}
                          onChange={(e) => setRecordForm({ ...recordForm, drugs: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  {/* MODIFIED PROTOCOL */}
                  {selectedMonitoring.protocol === PROTOCOL_MODIFIED && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Follicle"
                          value={recordForm.follicle}
                          onChange={(e) => setRecordForm({ ...recordForm, follicle: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Drugs"
                          value={recordForm.drugs}
                          onChange={(e) => setRecordForm({ ...recordForm, drugs: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  {/* HRT PROTOCOL */}
                  {selectedMonitoring.protocol === PROTOCOL_HRT && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Estrogen Dose"
                          value={recordForm.estrogen_dose}
                          onChange={(e) => setRecordForm({ ...recordForm, estrogen_dose: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Progesterone Dose"
                          value={recordForm.progesterone_start}
                          onChange={(e) => setRecordForm({ ...recordForm, progesterone_start: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  {/* STIMULATED PROTOCOL */}
                  {selectedMonitoring.protocol === PROTOCOL_STIMULATED && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Follicle"
                          value={recordForm.follicle}
                          onChange={(e) => setRecordForm({ ...recordForm, follicle: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Drugs"
                          value={recordForm.drugs}
                          onChange={(e) => setRecordForm({ ...recordForm, drugs: e.target.value.toUpperCase() })}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Remarks"
                      multiline
                      rows={2}
                      value={recordForm.remarks}
                      onChange={(e) => setRecordForm({ ...recordForm, remarks: e.target.value.toUpperCase() })}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* RECORDS TABLE */}
            {records && records.length > 0 ? (
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid ${primaryColor}30` }}>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow sx={{ height: 48, color: 'white !important' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Cycle Day</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Scan</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Endometrium</TableCell>
                      {selectedMonitoring.protocol === PROTOCOL_NATURAL && <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Ovulation</TableCell>}
                      {(selectedMonitoring.protocol === PROTOCOL_MODIFIED || selectedMonitoring.protocol === PROTOCOL_STIMULATED) && <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Follicle</TableCell>}
                      {selectedMonitoring.protocol === PROTOCOL_HRT && (
                        <>
                          <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>E2 Dose</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>P4 Dose</TableCell>
                        </>
                      )}
                      {(selectedMonitoring.protocol === PROTOCOL_NATURAL || selectedMonitoring.protocol === PROTOCOL_MODIFIED || selectedMonitoring.protocol === PROTOCOL_STIMULATED) && <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Drugs</TableCell>}
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Remarks</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem', textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                    {records.map((record, idx) => (
                      <TableRow 
                        key={record?.id ? `record-${record.id}` : `record-idx-${idx}`}
                        sx={{ 
                          '&:hover': { backgroundColor: `${primaryColor}08` },
                          borderBottom: `1px solid ${primaryColor}20`
                        }}
                      >
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{formatDateToDDMMYYYY(record?.date)}</TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.cycle_day || '-'}</TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.scan || '-'}</TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.endometrium || '-'}</TableCell>
                        {selectedMonitoring.protocol === PROTOCOL_NATURAL && <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.ovulation_status || '-'}</TableCell>}
                        {(selectedMonitoring.protocol === PROTOCOL_MODIFIED || selectedMonitoring.protocol === PROTOCOL_STIMULATED) && <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.follicle || '-'}</TableCell>}
                        {selectedMonitoring.protocol === PROTOCOL_HRT && (
                          <>
                            <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.estrogen_dose || '-'}</TableCell>
                            <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.progesterone_start || '-'}</TableCell>
                          </>
                        )}
                        {(selectedMonitoring.protocol === PROTOCOL_NATURAL || selectedMonitoring.protocol === PROTOCOL_MODIFIED || selectedMonitoring.protocol === PROTOCOL_STIMULATED) && <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.drugs || '-'}</TableCell>}
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{record?.remarks || '-'}</TableCell>
                        <TableCell sx={{ py: 1, textAlign: 'center' }}>
                          {canEdit && (
                            <Tooltip title="Edit Record">
                              <IconButton
                                size="small"
                                onClick={() => handleEditRecord(record)}
                                sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}15` } }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete Record">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRecord(record.id)}
                                sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f15' } }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  px: 2,
                  backgroundColor: `${primaryColor}08`,
                  borderRadius: 1,
                  border: `1px dashed ${primaryColor}40`,
                }}
              >
                <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                  No records added yet. Use the form above to add your first record.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ENDOMETRIAL ACCEPTABILITY VIEW */}
        {view === 'endometrial' && selectedMonitoring && (
          <Box>
            <Card sx={{ 
              mb: 3, 
              backgroundColor: `${primaryColor}12`,
              border: `2px solid ${primaryColor}30`
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: primaryColor }}>
                  🏥 Endometrial Acceptability Check
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Evaluate the endometrial acceptability based on the following criteria:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormGroup>
                        {clauseOptions && clauseOptions.length > 0 ? (
                          clauseOptions.map((opt, idx) => (
                            <Box 
                              key={opt?.id ? `clause-${opt.id}` : `clause-idx-${idx}`} 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between', 
                                p: 1.5,
                                mb: 1,
                                backgroundColor: '#fafafa',
                                border: `2px solid ${primaryColor}40`,
                                borderRadius: '6px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: `${primaryColor}08`,
                                  borderColor: `${primaryColor}80`
                                }
                              }}>
                              <Box sx={{ fontWeight: 600, color: '#222', fontSize: '0.95rem' }}>{opt.value}</Box>
                              <Checkbox
                                checked={endometrialForm[opt.value] || false}
                                onChange={(e) => setEndometrialForm({ ...endometrialForm, [opt.value]: e.target.checked })}
                                sx={{ 
                                  color: '#999',
                                  '&.Mui-checked': { 
                                    color: primaryColor,
                                  },
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '1.5rem',
                                    border: `2px solid #999`,
                                    borderRadius: '4px',
                                  },
                                  '&.Mui-checked .MuiSvgIcon-root': {
                                    border: `2px solid ${primaryColor}`,
                                  }
                                }}
                              />
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999', p: 1 }}>No clauses available</Typography>
                        )}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      value={endometrialForm.notes}
                      onChange={(e) => setEndometrialForm({ ...endometrialForm, notes: e.target.value })}
                      variant="outlined"
                      placeholder="Add any additional notes..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Latest Acceptability Records by Clause */}
            {endometrialAcceptabilities && endometrialAcceptabilities.length > 0 && (
              <Card sx={{ 
                mb: 3, 
                backgroundColor: `${primaryColor}12`,
                border: `2px solid ${primaryColor}30`
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: primaryColor }}>
                    ✓ Latest Assessment by Clause
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    {clauseOptions && clauseOptions.map((opt, idx) => {
                      const latestForClause = endometrialAcceptabilities
                        .filter(r => r.clause === opt.value && !r._deleted)
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
                      
                      return (
                        <Box 
                          key={opt?.id ? `latest-${opt.id}` : `latest-idx-${idx}`}
                          sx={{ 
                            p: 1.5, 
                            backgroundColor: latestForClause 
                              ? (latestForClause.acceptance === 'Accepted' ? '#e8f5e9' : '#ffebee') 
                              : '#fafafa',
                            borderRadius: '6px',
                            border: latestForClause 
                              ? `2px solid ${latestForClause.acceptance === 'Accepted' ? '#4CAF50' : '#f44336'}`
                              : '2px solid #ddd',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.2s ease'
                          }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ fontWeight: 600, mb: 0.5, color: '#222', fontSize: '0.95rem' }}>{opt.value}</Box>
                            {latestForClause ? (
                              <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                {formatDateToDDMMYYYY(latestForClause.created_at)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#999' }}>Not assessed</Typography>
                            )}
                          </Box>
                          {latestForClause && (
                            <Chip 
                              label={latestForClause.acceptance} 
                              size="small" 
                              sx={{ 
                                backgroundColor: latestForClause.acceptance === 'Accepted' ? '#4CAF50' : '#f44336', 
                                color: 'white', 
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                ml: 1,
                                '& .MuiChip-label': {
                                  px: 1.2,
                                  py: 0.5
                                }
                              }} 
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* History Table */}
            {endometrialAcceptabilities && endometrialAcceptabilities.filter(a => !a._deleted).length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>
                  📜 Assessment History ({endometrialAcceptabilities.filter(a => !a._deleted).length})
                </Typography>
              </Box>
            )}

            {endometrialAcceptabilities && endometrialAcceptabilities.filter(a => !a._deleted).length > 0 ? (
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, overflow: 'hidden', border: `1px solid ${primaryColor}30` }}>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow sx={{ height: 48, color: 'white !important' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Clause</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'white !important', backgroundColor: primaryColor, borderBottom: `2px solid rgba(255,255,255,0.2)`, fontSize: '0.85rem', textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                    {endometrialAcceptabilities.filter(a => !a._deleted).map((item, idx) => (
                      <TableRow 
                        key={item?.id ? `endometrial-${item.id}` : `endometrial-idx-${idx}`}
                        sx={{ 
                          '&:hover': { backgroundColor: `${primaryColor}08` },
                          borderBottom: `1px solid ${primaryColor}20`,
                        }}
                      >
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{formatDateToDDMMYYYY(item?.created_at)}</TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>{item?.clause || '-'}</TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.85rem' }}>
                          <Chip 
                            label={item?.acceptance} 
                            size="small" 
                            sx={{ 
                              backgroundColor: item?.acceptance === 'Accepted' ? '#4CAF50' : '#f44336', 
                              color: 'white', 
                              fontWeight: 700 
                            }} 
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1, textAlign: 'center' }}>
                          {!item._deleted && canEdit && (
                            <Tooltip title="Edit Assessment">
                              <IconButton
                                size="small"
                                onClick={() => handleEditEndometrialAcceptability(item)}
                                sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}15` } }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  px: 2,
                  backgroundColor: `${primaryColor}08`,
                  borderRadius: 1,
                  border: `1px dashed ${primaryColor}40`,
                }}
              >
                <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                  No assessments recorded yet. Use the form above to add your first assessment.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1.5, backgroundColor: '#f8f9fa', borderTop: `1px solid ${primaryColor}30` }}>
        {view === 'create' && (
          <>
            <Button 
              onClick={() => setView('list')}
              sx={{ 
                color: primaryColor, 
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 28px',
                fontSize: '14px',
                border: `2px solid ${primaryColor}`,
                backgroundColor: 'transparent',
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${primaryColor}08`,
                  boxShadow: `0 2px 8px ${primaryColor}40`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateMonitoring}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ 
                backgroundColor: primaryColor,
                color: 'white !important',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 32px',
                fontSize: '14px',
                boxShadow: `0 4px 14px ${primaryColor}60`,
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: primaryColor,
                  boxShadow: `0 6px 18px ${primaryColor}80`,
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: `0 2px 8px ${primaryColor}60`
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#ccc',
                  boxShadow: 'none'
                }
              }}
            >
              {saving ? 'Creating...' : 'Create Monitoring'}
            </Button>
          </>
        )}
        {view === 'edit' && (
          <>
            <Button 
              onClick={() => setView('list')}
              sx={{ 
                color: primaryColor, 
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 28px',
                fontSize: '14px',
                border: `2px solid ${primaryColor}`,
                backgroundColor: 'transparent',
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${primaryColor}08`,
                  boxShadow: `0 2px 8px ${primaryColor}40`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateMonitoring}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ 
                backgroundColor: primaryColor,
                color: 'white !important',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 32px',
                fontSize: '14px',
                boxShadow: `0 4px 14px ${primaryColor}60`,
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: primaryColor,
                  boxShadow: `0 6px 18px ${primaryColor}80`,
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: `0 2px 8px ${primaryColor}60`
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#999',
                  boxShadow: 'none'
                }
              }}
            >
              {saving ? 'Updating...' : 'Update Monitoring'}
            </Button>
          </>
        )}
        {view === 'records' && (
          <>
            <Button 
              onClick={() => {
                setView('list');
                resetRecordForm();
              }}
              sx={{ 
                color: primaryColor, 
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 28px',
                fontSize: '14px',
                border: `2px solid ${primaryColor}`,
                backgroundColor: 'transparent',
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${primaryColor}08`,
                  boxShadow: `0 2px 8px ${primaryColor}40`
                }
              }}
            >
              Back to List
            </Button>
            {editingRecordId && (
              <Button 
                onClick={() => {
                  resetRecordForm();
                }}
                sx={{ 
                  color: primaryColor, 
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '11px 28px',
                  fontSize: '14px',
                  border: `2px solid ${primaryColor}`,
                  backgroundColor: 'transparent',
                  letterSpacing: '0.3px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${primaryColor}08`,
                    boxShadow: `0 2px 8px ${primaryColor}40`
                  }
                }}
              >
                Clear Selection
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleAddRecord}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ 
                backgroundColor: primaryColor,
                color: 'white !important',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 32px',
                fontSize: '14px',
                boxShadow: `0 4px 14px ${primaryColor}60`,
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: primaryColor,
                  boxShadow: `0 6px 18px ${primaryColor}80`,
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: `0 2px 8px ${primaryColor}60`
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: 'white !important',
                  boxShadow: 'none'
                }
              }}
            >
              {saving ? 'Saving...' : (editingRecordId ? 'Update Record' : 'Add Record')}
            </Button>
          </>
        )}
        {view === 'endometrial' && (
          <>
            <Button 
              onClick={() => {
                setView('records');
                resetEndometrialForm();
              }}
              sx={{ 
                color: primaryColor, 
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 28px',
                fontSize: '14px',
                border: `2px solid ${primaryColor}`,
                backgroundColor: 'transparent',
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${primaryColor}08`,
                  boxShadow: `0 2px 8px ${primaryColor}40`
                }
              }}
            >
              Back to Records
            </Button>
            {editingEndometrialId && (
              <Button 
                onClick={() => {
                  resetEndometrialForm();
                }}
                sx={{ 
                  color: primaryColor, 
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '11px 28px',
                  fontSize: '14px',
                  border: `2px solid ${primaryColor}`,
                  backgroundColor: 'transparent',
                  letterSpacing: '0.3px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${primaryColor}08`,
                    boxShadow: `0 2px 8px ${primaryColor}40`
                  }
                }}
              >
                Clear Selection
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleAddEndometrialAcceptability}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ 
                backgroundColor: primaryColor,
                color: 'white !important',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                padding: '11px 32px',
                fontSize: '14px',
                boxShadow: `0 4px 14px ${primaryColor}60`,
                letterSpacing: '0.3px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: primaryColor,
                  boxShadow: `0 6px 18px ${primaryColor}80`,
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: `0 2px 8px ${primaryColor}60`
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: 'white !important',
                  boxShadow: 'none'
                }
              }}
            >
              {saving ? 'Saving...' : (editingEndometrialId ? 'Update Assessment' : 'Save Assessment')}
            </Button>
          </>
        )}
        {view === 'list' && (
          <Button 
            onClick={onClose}
            sx={{ 
              fontWeight: 700,
              color: primaryColor,
              textTransform: 'none',
              borderRadius: '8px',
              padding: '11px 28px',
              fontSize: '14px',
              border: `2px solid ${primaryColor}`,
              backgroundColor: 'transparent',
              letterSpacing: '0.3px',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: `${primaryColor}08`,
                boxShadow: `0 2px 8px ${primaryColor}40`
              }
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

EmbryoFetMonitoringModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procedureId: PropTypes.number.isRequired,
  visitId: PropTypes.number,
  contactId: PropTypes.number,
  primaryColor: PropTypes.string,
};

export default EmbryoFetMonitoringModal;
